import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore, useLayoutStore, useCardsStore } from '@/stores'

export default function Header() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { sidebarCollapsed } = useLayoutStore()
  const { getCard } = useCardsStore()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Check if we're on a card page and get the topic ID
  const getNewCardUrl = () => {
    const cardPageMatch = location.pathname.match(/^\/card\/(.+)$/)
    if (cardPageMatch && cardPageMatch[1] !== 'new') {
      const lid = decodeURIComponent(cardPageMatch[1])
      const card = getCard(lid)
      if (card) {
        return `/card/new?topic=${card.topicId}`
      }
    }
    return '/card/new'
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    setMenuOpen(false)
    logout()
    navigate('/login')
  }

  const handleBrandClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (location.pathname === '/dashboard') {
      navigate('/dashboard', { state: { reset: Date.now() }, replace: true })
    } else {
      navigate('/dashboard')
    }
  }

  const avatarLetter = user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'

  return (
    <header className="header">
      <div className="header-left">
        {sidebarCollapsed && (
          <div className="header-brand-group">
            <Link to="/dashboard" className="header-brand" onClick={handleBrandClick}>
              Branka
            </Link>
            <span className="header-slogan">Craft your ideas</span>
          </div>
        )}
      </div>

      <div className="header-right">
        <button
          className="btn btn-primary"
          onClick={() => navigate(getNewCardUrl())}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t('card.newCard')}
        </button>

        <div className="avatar-container" ref={menuRef}>
          <button
            className="avatar-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={t('auth.logout')}
          >
            {avatarLetter}
          </button>

          {menuOpen && (
            <div className="avatar-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-user-name">{user?.name || user?.email}</span>
                {user?.name && <span className="dropdown-user-email">{user?.email}</span>}
              </div>
              <div className="dropdown-divider" />
              <button onClick={handleLogout} className="dropdown-item dropdown-item--danger">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
                {t('auth.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
